import { Box, CircularProgress } from '@mui/material'
import type { ReactNode } from 'react'
import { Virtuoso } from 'react-virtuoso'
import { EmptyListState } from '../EmptyListState'
import type { WindowedCursorListRange } from '../../../hooks/useWindowedCursorList'

type VirtualCursorListProps<T> = {
  items: T[]
  firstItemIndex?: number
  hasMore?: boolean
  isLoadingMore?: boolean
  onLoadMore?: () => void
  onStartReached?: () => void
  onRangeChanged?: (range: WindowedCursorListRange) => void
  itemContent: (index: number, item: T) => ReactNode
  emptyMessage?: string
  getItemKey?: (index: number, item: T) => string | number
}

export function VirtualCursorList<T>({
  items,
  firstItemIndex = 0,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
  onStartReached,
  onRangeChanged,
  itemContent,
  emptyMessage = 'Нет данных для отображения',
  getItemKey,
}: VirtualCursorListProps<T>) {
  if (items.length === 0) {
    return <EmptyListState message={emptyMessage} />
  }

  return (
    <Virtuoso
      useWindowScroll
      data={items}
      firstItemIndex={firstItemIndex}
      {...(getItemKey ? { computeItemKey: getItemKey } : {})}
      {...(onRangeChanged ? { rangeChanged: onRangeChanged } : {})}
      {...(onLoadMore && hasMore ? { endReached: onLoadMore, atBottomThreshold: 300 } : {})}
      {...(onStartReached ? { startReached: onStartReached, atTopThreshold: 300 } : {})}
      overscan={200}
      itemContent={(index, item) => <Box sx={{ pb: 2 }}>{itemContent(index, item)}</Box>}
      components={{
        Footer: () =>
          hasMore && isLoadingMore ? (
            <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={24} />
            </Box>
          ) : null,
      }}
    />
  )
}
